import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { checkinAPI, api } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import StatsCard from '../components/Dashboard/StatsCard';
import MoodChart from '../components/Dashboard/MoodChart';
import AddictionImpact from '../components/Dashboard/AddictionImpact';
import QuickActions from '../components/Dashboard/QuickActions';
import OrganHealthOverview from '../components/Dashboard/OrganHealthOverview';

const Dashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    todayCheckin: null,
    weeklyMood: [],
    moodDataInfo: null,
    addictionImpact: null,
    organHealth: [],
    stats: {
      streak: 0,
      habitsCompleted: 0,
      moodAverage: 0,
      wellnessScore: 0,
      lastCheckin: null
    }
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load all data with individual error handling
      let todayCheckin = null;
      let currentStreak = 0;
      let weeklyMood = [];
      let moodDataInfo = null;
      let organHealth = [];
      let addictionImpact = null;
      
      try {
        const todayCheckinResponse = await checkinAPI.getToday();
        todayCheckin = todayCheckinResponse.data.checkin;
      } catch (error) {
        console.error('Failed to load today check-in:', error);
      }
      
      try {
        const streakResponse = await checkinAPI.getStreak();
        currentStreak = parseInt(streakResponse.data.streak) || 0;
      } catch (error) {
        console.error('Failed to load streak:', error);
      }
      
      try {
        const moodResponse = await checkinAPI.getStats('7d');
        weeklyMood = moodResponse.data?.data?.mood_trend || [];
        moodDataInfo = moodResponse.data?.data?.period_info || null;
      } catch (error) {
        console.error('Failed to load mood data:', error);
      }
      
      try {
        const organHealthResponse = await api.get('/organ-health/latest');
        organHealth = organHealthResponse.data.organHealth || [];
      } catch (error) {
        console.error('Failed to load organ health:', error);
      }
      
      try {
        const addictionImpactResponse = await checkinAPI.getAddictionImpact();
        addictionImpact = addictionImpactResponse.data?.data || null;
      } catch (error) {
        console.error('Failed to load addiction impact:', error);
      }
      
      // Calculate wellness score from organ health
      const validOrganScores = organHealth.filter(organ => organ && organ.health_score && !isNaN(organ.health_score) && organ.health_score > 0);
      const wellnessScore = validOrganScores.length > 0 
        ? validOrganScores.reduce((acc, organ) => acc + organ.health_score, 0) / validOrganScores.length
        : 0;
      
      // Calculate average mood from available check-ins only
      const validMoodScores = weeklyMood.filter(day => {
        const moodValue = day.mood || day.mood_score || day.score;
        const isValid = day && (moodValue !== undefined && moodValue !== null && moodValue !== '');
        return isValid;
      });
      
      const moodAverage = validMoodScores.length > 0 
        ? validMoodScores.reduce((acc, day) => {
            const moodValue = day.mood || day.mood_score || day.score;
            const numValue = parseFloat(moodValue);
            return acc + numValue;
          }, 0) / validMoodScores.length
        : 0;
      
      // Calculate stats
      const stats = {
        streak: currentStreak, // Use dedicated streak API instead of today's checkin streak
        habitsCompleted: 0,
        moodAverage: moodAverage,
        wellnessScore: Math.round(wellnessScore),
        lastCheckin: todayCheckin?.date || null
      };
      
      setDashboardData({
        todayCheckin,
        weeklyMood,
        moodDataInfo,
        addictionImpact,
        organHealth,
        stats
      });
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Daily quotes - 7 quotes that cycle every week
  const dailyQuotes = [
    { text: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
    { text: "Believe you can and you're halfway there.", author: "Theodore Roosevelt" },
    { text: "Every moment is a fresh beginning.", author: "T.S. Eliot" },
    { text: "Change your thoughts and you change your world.", author: "Norman Vincent Peale" },
    { text: "Happiness depends upon ourselves.", author: "Aristotle" },
    { text: "Turn your wounds into wisdom.", author: "Oprah Winfrey" },
    { text: "It is never too late to be what you might have been.", author: "George Eliot" }
  ];

  // Get today's quote based on day of year
  const getTodaysQuote = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 0);
    const diff = now - start;
    const oneDay = 1000 * 60 * 60 * 24;
    const dayOfYear = Math.floor(diff / oneDay);
    return dailyQuotes[dayOfYear % dailyQuotes.length];
  };

  const todaysQuote = getTodaysQuote();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white shadow-lg">
        <h1 className="text-3xl font-bold mb-2">
          Welcome back, {user?.name}! 👋
        </h1>
        <p className="text-blue-100 text-lg">
          Here's your wellness overview for today. Keep up the great work!
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Daily Streak"
          value={dashboardData.stats.streak}
          subtitle="Days in a row"
          icon="🔥"
          color="orange"
        />
        <StatsCard
          title="Avg Mood"
          value={dashboardData.stats.moodAverage}
          subtitle={`${dashboardData.weeklyMood.length} days`}
          icon="😊"
          color="blue"
        />

        {/* Daily Quote Card - Full Width */}
        <div className="md:col-span-1 lg:col-span-2 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl p-6 text-white shadow-lg flex flex-col justify-center">
          <div className="flex items-start gap-3">
            <span className="text-3xl">💭</span>
            <div className="flex-1">
              <p className="text-lg font-medium leading-relaxed italic">
                "{todaysQuote.text}"
              </p>
              <p className="text-purple-200 text-sm mt-2">— {todaysQuote.author}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Mood Chart */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-md p-6 h-full flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Weekly Mood Trend</h2>
            </div>
            <div className="flex-1">
              <MoodChart data={dashboardData.weeklyMood} />
              {dashboardData.weeklyMood.length === 0 && (
                <div className="mt-4 text-center text-gray-500 text-sm">
                  No mood data available. Complete daily check-ins to see your mood trends.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Addiction Impact */}
        <div className="lg:col-span-1 h-full">
          <AddictionImpact addictions={dashboardData.addictionImpact?.addictions || []} />
        </div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-md p-6 h-full">
            <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
            <QuickActions onActionComplete={loadDashboardData} />
          </div>
        </div>

        {/* Organ Health Overview */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-md p-6 h-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Organ Health Overview</h2>
            </div>
            <div className="flex-1">
              <OrganHealthOverview data={dashboardData.organHealth} />
            </div>
          </div>
        </div>
      </div>

      {/* Today's Check-in Status */}
      {dashboardData.todayCheckin ? (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-green-800">
                Today's Check-in Complete!
              </h3>
              <p className="text-green-700">
                Great job staying consistent with your wellness tracking. Current streak: {dashboardData.stats.streak} days 🔥
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-yellow-800">
                Daily Check-in Pending
              </h3>
              <p className="text-yellow-700">
                Take a moment to complete your daily wellness check-in.
              </p>
              <button 
                onClick={() => window.location.href = '/checkin'}
                className="mt-2 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
              >
                Complete Check-in
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
